import { Camera, FileText, Play, Trash2, Video } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/utils'

/** Winner judge evaluation data */
export interface WinnerJudge {
  /** Winner persona identifier (e.g., "hawk", "oracle") */
  persona: string
  /** Human-readable label for the persona */
  label: string
}

export interface PitchCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Pitch ID for routing */
  pitchId: string
  /** Total pitch score (0-100) */
  totalScore?: number
  /** Video cover image URL */
  videoCoverImageUrl?: string | null
  /** Pitch completion date */
  finishedAt: string | Date
  /** Individual judge scores (metric scores for winner persona) */
  judgeScores?: Array<{
    metric: string
    label: string
    score: number
  }>
  /** Winner judge information */
  winnerJudge?: WinnerJudge | null
  /** Associated pitch deck document */
  pitchDeck?: {
    id: string
    fileName: string
    label: string
  } | null
  /** Show placeholder image when no cover image */
  showPlaceholder?: boolean
  /** Optional click handler for the video area */
  onVideoClick?: () => void
  /** Optional click handler for cover image upload (edit mode) */
  onCoverImageUpload?: () => void
  /** Optional click handler for cover image removal */
  onCoverImageRemove?: () => void
  /** Optional render prop for the view details button */
  renderViewDetailsButton?: (pitchId: string) => React.ReactNode
  /** Optional click handler for pitch deck */
  onPitchDeckClick?: (pitchDeck: { id: string; fileName: string; label: string }) => void
  /** Optional actions to render in the footer area (replaces date when provided) */
  footerActions?: React.ReactNode
  /** Override text above scores (replaces default winner judge text) */
  scoreLabel?: React.ReactNode
}

function CoverImageOverlay({
  onUpload,
  onRemove,
  hasImage,
}: {
  onUpload?: () => void
  onRemove?: () => void
  hasImage?: boolean
}) {
  if (!onUpload && !(onRemove && hasImage)) return null
  return (
    <div className="absolute bottom-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
      {onUpload && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onUpload()
          }}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/20 bg-black/70 backdrop-blur-sm text-white text-xs font-medium transition-all hover:bg-white/20 hover:border-white/40"
        >
          <Camera className="size-3.5" />
          <span>Change Cover</span>
        </button>
      )}
      {onRemove && hasImage && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onRemove()
          }}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-red-400/30 bg-black/70 backdrop-blur-sm text-red-400 text-xs font-medium transition-all hover:bg-red-500/20 hover:border-red-400/50"
        >
          <Trash2 className="size-3.5" />
          <span>Remove</span>
        </button>
      )}
    </div>
  )
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-emerald-400'
  if (score >= 60) return 'text-amber-400'
  return 'text-red-400'
}

export const PitchCard = React.forwardRef<HTMLDivElement, PitchCardProps>(
  (
    {
      pitchId,
      totalScore,
      videoCoverImageUrl,
      finishedAt,
      judgeScores,
      winnerJudge,
      pitchDeck,
      showPlaceholder = false,
      onVideoClick,
      onCoverImageUpload,
      onCoverImageRemove,
      renderViewDetailsButton,
      onPitchDeckClick,
      footerActions,
      scoreLabel,
      className,
      ...props
    },
    ref
  ) => {
    const formattedDate = new Date(finishedAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

    const [videoPlayed, setVideoPlayed] = React.useState(false)

    const handleVideoClick = () => {
      if (onVideoClick) {
        setVideoPlayed(true)
        onVideoClick()
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border bg-input/50',
          className
        )}
        {...props}
      >
        <div className="flex flex-col md:flex-row">
          {/* 16:9 Video Thumbnail */}
          {onVideoClick ? (
            <button
              type="button"
              className="group relative aspect-video md:w-2/3 md:shrink-0 bg-linear-to-br from-secondary to-card overflow-hidden cursor-pointer"
              onClick={handleVideoClick}
            >
              {videoCoverImageUrl || showPlaceholder ? (
                <img
                  src={videoCoverImageUrl || 'https://unsplash.it/640/360'}
                  alt="Pitch recording"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                  <Video className="size-10 opacity-20" />
                  <span className="text-xs opacity-40">No preview available</span>
                </div>
              )}

              {/* Play CTA - always visible */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative flex items-center justify-center size-20">
                  {!videoPlayed && (
                    <div className="absolute inset-0 rounded-full border-2 border-primary bg-primary/20 animate-ping opacity-70" />
                  )}
                  <div
                    className={cn(
                      'flex items-center justify-center size-20 rounded-full border-2 border-primary transition-all duration-300 ease-out group-hover:scale-120',
                      videoPlayed ? 'bg-black/20 group-hover:bg-primary' : 'bg-primary'
                    )}
                  >
                    <Play
                      className={cn(
                        'size-9 ml-1 transition-colors',
                        videoPlayed
                          ? 'text-white fill-none stroke-[1.5] group-hover:fill-white'
                          : 'text-white fill-white'
                      )}
                    />
                  </div>
                </div>
              </div>

              <CoverImageOverlay
                onUpload={onCoverImageUpload}
                onRemove={onCoverImageRemove}
                hasImage={!!videoCoverImageUrl}
              />
            </button>
          ) : (
            <div className="group relative aspect-video md:w-2/3 md:shrink-0 bg-linear-to-br from-secondary to-card overflow-hidden">
              {videoCoverImageUrl || showPlaceholder ? (
                <img
                  src={videoCoverImageUrl || 'https://unsplash.it/640/360'}
                  alt="Pitch recording"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                  <Video className="size-10 opacity-20" />
                  <span className="text-xs opacity-40">No preview available</span>
                </div>
              )}
              <CoverImageOverlay
                onUpload={onCoverImageUpload}
                onRemove={onCoverImageRemove}
                hasImage={!!videoCoverImageUrl}
              />
            </div>
          )}

          {/* Scores Panel */}
          <div className="md:w-1/3 p-5 flex flex-col border-t md:border-t-0 md:border-l border-border">
            {/* Top: Score label text */}
            {(scoreLabel || winnerJudge) && (
              <p className="text-center text-sm text-muted-foreground leading-relaxed">
                {scoreLabel ?? (
                  <>
                    After evaluating all relevant data,{' '}
                    <span className="font-semibold text-white">{winnerJudge?.label}</span> was the
                    most impressed by this pitch.
                  </>
                )}
              </p>
            )}

            {/* Center: Total Score */}
            {totalScore != null && (
              <div className="flex-1 flex flex-col items-center justify-center">
                <div className={cn('text-4xl font-bold', getScoreColor(totalScore))}>
                  {totalScore}
                </div>
                <div className="text-sm text-muted-foreground">Total Score</div>
              </div>
            )}

            {/* Bottom: Judge Scores + Button + Pitch Deck */}
            <div>
              {/* Metric Scores (for winner persona) */}
              {judgeScores && judgeScores.length > 0 && (
                <div className="space-y-2">
                  {judgeScores.map((score) => (
                    <div key={score.metric} className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{score.label}</span>
                      <span className={cn('text-sm font-semibold', getScoreColor(score.score))}>
                        {score.score}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* View Details Button */}
              {renderViewDetailsButton && (
                <div className="mt-4">{renderViewDetailsButton(pitchId)}</div>
              )}

              {/* Footer: Actions or Date + Pitch Deck Link */}
              <div className="mt-4 pt-4 border-t border-border">
                {footerActions ?? (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{formattedDate}</span>
                    {pitchDeck && (
                      <button
                        type="button"
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors"
                        onClick={(e) => {
                          e.stopPropagation()
                          onPitchDeckClick?.(pitchDeck)
                        }}
                      >
                        <FileText className="size-4" />
                        <span className="truncate">{pitchDeck.label}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
)

PitchCard.displayName = 'PitchCard'
